/**
 * Centralized campaign attribution capture and lead attachment (Milestone 27).
 */
import {
  ATTRIBUTION_LIMITS,
  ATTRIBUTION_STORAGE_KEYS,
  CLICK_ID_PARAMS,
  UTM_PARAMS,
} from "../constants/campaignAttributionConstants";
import { getPageAttributionContext } from "./pageAttributionContext";
import { trackAttributionEvent } from "./campaignAttributionAnalytics";

function safeStorage(get, set) {
  try {
    return { get, set, available: true };
  } catch {
    return {
      get: () => null,
      set: () => {},
      available: false,
    };
  }
}

const storage = safeStorage(
  (key) => localStorage.getItem(key),
  (key, value) => localStorage.setItem(key, value)
);

function randomId(prefix = "am") {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function readJson(key) {
  const raw = storage.get(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeJson(key, value) {
  storage.set(key, JSON.stringify(value));
}

function truncate(value, max = ATTRIBUTION_LIMITS.MAX_UTM_LENGTH) {
  if (!value) return null;
  const text = String(value).trim();
  return text.length > max ? text.slice(0, max) : text;
}

export function getReferrerDomain(referrer) {
  if (!referrer) return null;
  try {
    return new URL(referrer).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

function isSameSiteReferrer(referrer) {
  if (!referrer || typeof window === "undefined") return true;
  try {
    const refHost = new URL(referrer).hostname;
    return refHost === window.location.hostname;
  } catch {
    return true;
  }
}

export function parseUrlAttributionParams(url) {
  let parsed;
  try {
    parsed = typeof url === "string" ? new URL(url, "http://local") : url;
  } catch {
    return {};
  }

  const params = {};
  UTM_PARAMS.forEach((key) => {
    const short = key.replace("utm_", "");
    const value = parsed.searchParams.get(key);
    if (value) params[short === "source" ? "utmSource" : short === "medium" ? "utmMedium" : short === "campaign" ? "utmCampaign" : short === "content" ? "utmContent" : "utmTerm"] = truncate(value);
  });

  CLICK_ID_PARAMS.forEach((key) => {
    const value = parsed.searchParams.get(key);
    if (value) params[key] = truncate(value);
  });

  return params;
}

export function inferTrafficSource({
  utmSource,
  utmMedium,
  gclid,
  fbclid,
  ttclid,
  msclkid,
  referrer,
  pagePath,
  pageType,
} = {}) {
  if (utmSource) {
    return { source: utmSource, medium: utmMedium || "unknown", campaign: null };
  }
  if (gclid) return { source: "google", medium: "cpc" };
  if (fbclid) return { source: "facebook", medium: "social" };
  if (ttclid) return { source: "tiktok", medium: "paid_social" };
  if (msclkid) return { source: "bing", medium: "cpc" };

  const ref = (referrer || "").toLowerCase();
  const refDomain = getReferrerDomain(referrer);

  if (ref.includes("google.")) return { source: "google", medium: "organic" };
  if (ref.includes("bing.")) return { source: "bing", medium: "organic" };
  if (ref.includes("facebook.") || ref.includes("instagram.")) return { source: "meta", medium: "social" };
  if (ref.includes("whatsapp.")) return { source: "whatsapp", medium: "referral" };

  if (String(pageType || "").startsWith("seo_") && ref && !isSameSiteReferrer(referrer)) {
    return { source: "organic_search", medium: "seo" };
  }

  if (ref && !isSameSiteReferrer(referrer)) {
    return { source: refDomain || "referral", medium: "referral" };
  }

  if (!ref && !utmSource && !gclid && !fbclid) {
    return { source: "direct", medium: "none" };
  }

  return { source: "direct", medium: "none" };
}

function buildTouchFromInput(input = {}) {
  const page = input.pagePath
    ? getPageAttributionContext(input.pagePath)
    : typeof window !== "undefined"
      ? getPageAttributionContext(window.location.pathname)
      : {};

  const urlParams =
    input.urlParams ||
    (typeof window !== "undefined" ? parseUrlAttributionParams(window.location.href) : {});

  const referrer = input.referrer ?? (typeof document !== "undefined" ? document.referrer : null);
  const inferred = inferTrafficSource({
    utmSource: urlParams.utmSource,
    utmMedium: urlParams.utmMedium,
    gclid: urlParams.gclid,
    fbclid: urlParams.fbclid,
    ttclid: urlParams.ttclid,
    msclkid: urlParams.msclkid,
    referrer,
    pagePath: page.pagePath,
    pageType: page.pageType,
  });

  const now = new Date().toISOString();
  return {
    id: randomId("touch"),
    sessionId: getAttributionSessionId(),
    visitorId: getAttributionVisitorId(),
    pagePath: page.pagePath || input.pagePath || null,
    pageType: page.pageType || input.pageType || "unknown",
    landingPage: input.landingPage || page.landingPagePath || page.pagePath || null,
    landingPageType: input.landingPageType || page.landingPageType || page.pageType || null,
    landingPagePath: page.landingPagePath || page.pagePath || null,
    sourceComponent: input.sourceComponent || null,
    sourceAction: input.sourceAction || null,
    referrer: referrer || null,
    referrerDomain: getReferrerDomain(referrer),
    utmSource: urlParams.utmSource || null,
    utmMedium: urlParams.utmMedium || null,
    utmCampaign: urlParams.utmCampaign || null,
    utmContent: urlParams.utmContent || null,
    utmTerm: urlParams.utmTerm || null,
    gclid: urlParams.gclid || null,
    fbclid: urlParams.fbclid || null,
    ttclid: urlParams.ttclid || null,
    msclkid: urlParams.msclkid || null,
    source: inferred.source,
    medium: inferred.medium,
    campaign: urlParams.utmCampaign || null,
    dealerId: input.dealerId || page.dealerId || null,
    listingId: input.listingId || page.listingId || null,
    seoPageType: page.seoPageType || input.seoPageType || null,
    seoPageSlug: page.seoPageSlug || input.seoPageSlug || null,
    guideSlug: page.guideSlug || input.guideSlug || null,
    eventName: input.eventName || "page_view",
    eventPayload: input.eventPayload || null,
    createdAt: now,
  };
}

function isExpired(record) {
  if (!record?.createdAt) return true;
  const age = Date.now() - new Date(record.createdAt).getTime();
  return age > ATTRIBUTION_LIMITS.CONTEXT_RETENTION_MS;
}

export function getAttributionVisitorId() {
  let id = readJson(ATTRIBUTION_STORAGE_KEYS.VISITOR_ID);
  if (typeof id === "string") return id;
  id = readJson(ATTRIBUTION_STORAGE_KEYS.VISITOR_ID) || storage.get(ATTRIBUTION_STORAGE_KEYS.VISITOR_ID);
  if (id) return id;
  const next = randomId("visitor");
  writeJson(ATTRIBUTION_STORAGE_KEYS.VISITOR_ID, next);
  storage.set(ATTRIBUTION_STORAGE_KEYS.VISITOR_ID, next);
  return next;
}

export function getAttributionSessionId() {
  const session = readJson(ATTRIBUTION_STORAGE_KEYS.SESSION);
  const now = Date.now();
  if (session?.sessionId && session.lastActivityAt) {
    const idle = now - Number(session.lastActivityAt);
    if (idle < ATTRIBUTION_LIMITS.SESSION_INACTIVITY_MS) {
      writeJson(ATTRIBUTION_STORAGE_KEYS.SESSION, { ...session, lastActivityAt: now });
      return session.sessionId;
    }
  }
  const sessionId = randomId("session");
  writeJson(ATTRIBUTION_STORAGE_KEYS.SESSION, { sessionId, lastActivityAt: now, startedAt: now });
  storage.set(ATTRIBUTION_STORAGE_KEYS.SESSION_ID, sessionId);
  return sessionId;
}

function touchHasMarketingSignal(touch) {
  return Boolean(
    touch?.utmSource ||
      touch?.utmMedium ||
      touch?.utmCampaign ||
      touch?.gclid ||
      touch?.fbclid ||
      touch?.ttclid ||
      touch?.msclkid ||
      (touch?.referrer && !isSameSiteReferrer(touch.referrer))
  );
}

export function getFirstTouch() {
  const touch = readJson(ATTRIBUTION_STORAGE_KEYS.FIRST_TOUCH);
  return touch && !isExpired(touch) ? touch : null;
}

export function getLastTouch() {
  const touch = readJson(ATTRIBUTION_STORAGE_KEYS.LAST_TOUCH);
  return touch && !isExpired(touch) ? touch : null;
}

export function getCurrentTouch() {
  return getLastTouch() || getFirstTouch();
}

export function getStoredAttributionContext() {
  const firstTouch = getFirstTouch();
  const lastTouch = getLastTouch();
  const landing = readJson(ATTRIBUTION_STORAGE_KEYS.LANDING);
  const conversionPath = readJson(ATTRIBUTION_STORAGE_KEYS.CONVERSION_PATH) || [];

  return {
    firstTouch,
    lastTouch,
    currentTouch: lastTouch || firstTouch,
    landingPage: landing?.landingPage || firstTouch?.landingPage || null,
    landingPageType: landing?.landingPageType || firstTouch?.landingPageType || null,
    landingPagePath: landing?.landingPagePath || firstTouch?.landingPagePath || null,
    conversionPath,
    attributionSessionId: getAttributionSessionId(),
    attributionVisitorId: getAttributionVisitorId(),
  };
}

function appendConversionPath(item) {
  const path = readJson(ATTRIBUTION_STORAGE_KEYS.CONVERSION_PATH) || [];
  path.push(item);
  while (path.length > ATTRIBUTION_LIMITS.MAX_CONVERSION_PATH) {
    path.shift();
  }
  writeJson(ATTRIBUTION_STORAGE_KEYS.CONVERSION_PATH, path);
}

export function updateAttributionContext(touchpointInput = {}) {
  const touch = buildTouchFromInput(touchpointInput);
  const firstTouch = getFirstTouch();
  const hasMarketing = touchHasMarketingSignal(touch);

  if (!firstTouch || isExpired(firstTouch)) {
    writeJson(ATTRIBUTION_STORAGE_KEYS.FIRST_TOUCH, touch);
    writeJson(ATTRIBUTION_STORAGE_KEYS.LANDING, {
      landingPage: touch.landingPage,
      landingPageType: touch.landingPageType,
      landingPagePath: touch.landingPagePath,
      createdAt: touch.createdAt,
    });
    trackAttributionEvent("attribution_context_created", { pageType: touch.pageType });
  } else if (hasMarketing && !isSameSiteReferrer(touch.referrer)) {
    // External/marketing arrival updates last touch but preserves first touch.
    writeJson(ATTRIBUTION_STORAGE_KEYS.LAST_TOUCH, touch);
    trackAttributionEvent("attribution_context_updated", { reason: "marketing_signal" });
  }

  if (!readJson(ATTRIBUTION_STORAGE_KEYS.LAST_TOUCH) || hasMarketing) {
    writeJson(ATTRIBUTION_STORAGE_KEYS.LAST_TOUCH, touch);
  } else {
    writeJson(ATTRIBUTION_STORAGE_KEYS.LAST_TOUCH, {
      ...getLastTouch(),
      ...touch,
      utmSource: touch.utmSource || getLastTouch()?.utmSource,
      utmMedium: touch.utmMedium || getLastTouch()?.utmMedium,
      utmCampaign: touch.utmCampaign || getLastTouch()?.utmCampaign,
      source: touch.source,
      medium: touch.medium,
      updatedAt: touch.createdAt,
    });
  }

  appendConversionPath({
    pagePath: touch.pagePath,
    pageType: touch.pageType,
    eventName: touch.eventName,
    sourceComponent: touch.sourceComponent,
    sourceAction: touch.sourceAction,
    createdAt: touch.createdAt,
  });

  trackAttributionEvent("attribution_touchpoint_recorded", {
    pageType: touch.pageType,
    eventName: touch.eventName,
  });

  if (hasMarketing) {
    trackAttributionEvent("campaign_url_with_utm_detected", {
      utmSource: touch.utmSource,
      utmMedium: touch.utmMedium,
      utmCampaign: touch.utmCampaign,
    });
  }

  return touch;
}

export function captureAttributionFromCurrentUrl(overrides = {}) {
  if (typeof window === "undefined") return null;
  return updateAttributionContext({
    ...overrides,
    pagePath: overrides.pagePath || window.location.pathname,
    referrer: overrides.referrer ?? document.referrer,
    urlParams: overrides.urlParams || parseUrlAttributionParams(window.location.href),
    eventName: overrides.eventName || "page_view",
  });
}

export function getConversionAttributionContext(conversionDetails = {}) {
  const stored = getStoredAttributionContext();
  const first = stored.firstTouch || {};
  const last = stored.lastTouch || first;
  const conversionTouch = buildTouchFromInput({
    ...conversionDetails,
    pagePath: conversionDetails.pagePath || (typeof window !== "undefined" ? window.location.pathname : null),
    eventName: conversionDetails.eventName || "lead_conversion",
  });

  const pathSummary = (stored.conversionPath || [])
    .slice(-5)
    .map((item) => item.pagePath)
    .filter(Boolean)
    .join(" → ");

  return {
    ...stored,
    conversionTouch,
    conversionPage: conversionTouch.pagePath,
    conversionPageType: conversionTouch.pageType,
    conversionPath: stored.conversionPath || [],
    conversionPathSummary: pathSummary,
    source: last.source || first.source,
    medium: last.medium || first.medium,
    campaign: last.campaign || first.campaign || last.utmCampaign || first.utmCampaign,
  };
}

export function attachAttributionToLead(leadInput = {}) {
  const conversion = getConversionAttributionContext({
    sourceComponent: leadInput.sourceComponent,
    sourceAction: leadInput.sourceAction,
    pagePath: leadInput.pagePath,
    listingId: leadInput.listingId || leadInput.listing?.vehicleListingId,
    dealerId: leadInput.dealerId,
    seoPageType: leadInput.seoPageType,
    seoPageSlug: leadInput.seoPageSlug,
    guideSlug: leadInput.guideSlug,
  });

  const first = conversion.firstTouch || {};
  const last = conversion.lastTouch || first;
  const current = conversion.conversionTouch || last;

  const attached = {
    ...leadInput,
    sourcePage: leadInput.sourcePage || current.pagePath || last.pagePath || first.pagePath,
    landingPage: first.landingPage || first.pagePath || conversion.landingPage,
    landingPageType: first.landingPageType || conversion.landingPageType,
    landingPagePath: first.landingPagePath || conversion.landingPagePath,
    referrer: last.referrer || first.referrer || null,
    referrerDomain: last.referrerDomain || first.referrerDomain || null,
    utmSource: last.utmSource || first.utmSource || null,
    utmMedium: last.utmMedium || first.utmMedium || null,
    utmCampaign: last.utmCampaign || first.utmCampaign || null,
    utmContent: last.utmContent || first.utmContent || null,
    utmTerm: last.utmTerm || first.utmTerm || null,
    gclid: last.gclid || first.gclid || null,
    fbclid: last.fbclid || first.fbclid || null,
    ttclid: last.ttclid || first.ttclid || null,
    msclkid: last.msclkid || first.msclkid || null,
    sourceCampaign: last.utmCampaign || first.utmCampaign || null,
    firstTouchSource: first.source || null,
    firstTouchMedium: first.medium || null,
    firstTouchCampaign: first.utmCampaign || first.campaign || null,
    lastTouchSource: last.source || null,
    lastTouchMedium: last.medium || null,
    lastTouchCampaign: last.utmCampaign || last.campaign || null,
    conversionPage: conversion.conversionPage,
    conversionPageType: conversion.conversionPageType,
    conversionPath: conversion.conversionPath,
    conversionPathSummary: conversion.conversionPathSummary,
    attributionSessionId: conversion.attributionSessionId,
    attributionVisitorId: conversion.attributionVisitorId,
    seoPageType: first.seoPageType || last.seoPageType || current.seoPageType || leadInput.seoPageType || null,
    seoPageSlug: first.seoPageSlug || last.seoPageSlug || current.seoPageSlug || leadInput.seoPageSlug || null,
    guideSlug: first.guideSlug || last.guideSlug || current.guideSlug || leadInput.guideSlug || null,
    pageType: current.pageType || last.pageType || null,
    pagePath: current.pagePath || last.pagePath || null,
  };

  trackAttributionEvent("attribution_lead_attached", {
    sourceComponent: attached.sourceComponent,
    sourceAction: attached.sourceAction,
    firstTouchSource: attached.firstTouchSource,
    conversionPageType: attached.conversionPageType,
  });

  if (!attached.firstTouchSource && !attached.referrer && !attached.utmSource) {
    trackAttributionEvent("attribution_missing_on_lead", { leadType: leadInput.leadType });
  } else {
    trackAttributionEvent("lead_created_with_attribution", { leadType: leadInput.leadType });
  }

  return attached;
}

export function clearAttributionContext() {
  Object.values(ATTRIBUTION_STORAGE_KEYS).forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  });
}

export function buildCampaignUrl(baseUrl, { source, medium, campaign, content, term } = {}) {
  const url = new URL(baseUrl, typeof window !== "undefined" ? window.location.origin : "https://automapa.co");
  if (source) url.searchParams.set("utm_source", source);
  if (medium) url.searchParams.set("utm_medium", medium);
  if (campaign) url.searchParams.set("utm_campaign", campaign);
  if (content) url.searchParams.set("utm_content", content);
  if (term) url.searchParams.set("utm_term", term);
  return url.toString();
}
